import { useState } from 'react';
import { Trash2, Plus, Eye, Sparkles, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Lightbox } from './Lightbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Manager per le foto generate con IA (staging virtuale, digital twin, ecc.).
 * Separato dalle foto reali per non ingannare il cliente finale — verranno
 * mostrate in una sezione dedicata dell'annuncio pubblico.
 */
export function AIImageManager({ propertyId, aiImages = [], onUpdate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localImages, setLocalImages] = useState(aiImages);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openManager = () => {
    setLocalImages([...aiImages]);
    setIsOpen(true);
  };

  const getUrl = (path) => `${API}/files/${path}?v=3`;

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      const newPaths = [];
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append('file', files[i]);
        const { data } = await axios.post(`${API}/properties/${propertyId}/ai-images`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newPaths.push(data.path);
      }
      setLocalImages((prev) => [...prev, ...newPaths]);
      toast.success(`${files.length} foto IA caricata/e`);
      onUpdate();
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (path) => {
    try {
      await axios.delete(`${API}/properties/${propertyId}/ai-images?image_path=${encodeURIComponent(path)}`, {
        withCredentials: true,
      });
      setLocalImages((prev) => prev.filter((p) => p !== path));
      toast.success('Foto IA rimossa');
      onUpdate();
    } catch {
      toast.error('Errore nella rimozione');
    }
  };

  const moveImage = async (index, direction) => {
    const newList = [...localImages];
    const target = index + direction;
    if (target < 0 || target >= newList.length) return;
    [newList[index], newList[target]] = [newList[target], newList[index]];
    setLocalImages(newList);
    try {
      await axios.put(
        `${API}/properties/${propertyId}/ai-images/reorder`,
        { ai_images: newList },
        { withCredentials: true }
      );
      onUpdate();
    } catch {
      toast.error('Errore nel riordino');
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={openManager}
        title="Gestisci foto generate con IA"
        data-testid={`manage-ai-images-${propertyId}`}
      >
        <Sparkles size={16} className="text-purple-500" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Sparkles size={20} className="text-purple-500" />
              Foto generate con Intelligenza Artificiale
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="font-sans text-sm text-text-muted">
              Carica qui le foto generate con IA (staging virtuale, ristrutturazioni simulate, ecc.).
              Saranno mostrate in una sezione dedicata separata dalle foto reali.
            </p>

            <label
              className="flex items-center justify-center border-2 border-dashed border-purple-300 rounded-xl p-6 cursor-pointer hover:bg-purple-50 transition-colors"
              data-testid="ai-image-upload-area"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && handleUpload(e.target.files)}
                disabled={uploading}
              />
              <div className="text-center">
                <Sparkles size={24} className="mx-auto text-purple-500 mb-2" />
                <p className="font-sans text-sm text-text-muted">
                  {uploading ? 'Caricamento in corso...' : 'Clicca per aggiungere foto generate con IA'}
                </p>
                <p className="font-sans text-xs text-text-muted mt-1">Puoi selezionare più immagini</p>
              </div>
            </label>

            {localImages.length > 0 ? (
              <>
                <p className="font-sans text-sm text-text-muted">
                  {localImages.length} foto IA caricata/e — usa le frecce per riordinarle
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {localImages.map((img, index) => (
                    <div key={img} className="relative group rounded-lg overflow-hidden border border-purple-200">
                      <div className="aspect-[4/3] relative">
                        <img
                          src={getUrl(img)}
                          alt={`Foto IA ${index + 1}`}
                          className="w-full h-full object-cover bg-gray-50"
                        />
                        <div className="absolute top-1 left-1 bg-purple-500/90 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Sparkles size={10} /> IA #{index + 1}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                          <button
                            onClick={() => moveImage(index, -1)}
                            disabled={index === 0}
                            className="p-1.5 bg-white/90 rounded-full hover:bg-white disabled:opacity-40"
                            title="Sposta a sinistra"
                          >
                            <ArrowLeft size={14} />
                          </button>
                          <button
                            onClick={() => setLightboxIndex(index)}
                            className="p-1.5 bg-white/90 rounded-full hover:bg-white"
                            title="Anteprima"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => moveImage(index, 1)}
                            disabled={index === localImages.length - 1}
                            className="p-1.5 bg-white/90 rounded-full hover:bg-white disabled:opacity-40"
                            title="Sposta a destra"
                          >
                            <ArrowRight size={14} />
                          </button>
                          <button
                            onClick={() => handleRemove(img)}
                            className="p-1.5 bg-red-500/90 rounded-full hover:bg-red-500 text-white"
                            title="Elimina"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-text-muted font-sans py-6">
                Nessuna foto IA caricata per questo annuncio
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {lightboxIndex !== null && (
        <Lightbox
          images={localImages.map(getUrl)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
